import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { getFacilities } from 'app/state/ducks/facilities/selectors'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getEntityTypes } from 'app/state/ducks/entity-type/selectors'
import { getWireInstructions } from 'app/state/ducks/wire-instructions/selectors'
import {
  getTradeDetailDeals,
  getTradeDetailEntities,
  getTradeDetailEntityTypes,
  getTradeDetailFacilities,
  getTradeDetailWireInstructions,
} from 'app/state/ducks/trade-detail/selectors'

/**
 * What the Trade detail page reads for deals, facilities, entities, entity
 * types and wire instructions (ADR_LOS-030): the trade's own lookups from the
 * Sub-trade read, merged by id with the shared store. The shared store is
 * empty on a deep link, full once an editor has loaded a whole list on open
 * (the allocation editor's fund picker) — and full of whatever any EARLIER
 * page loaded, which may predate this trade's read. So the trade's own item
 * wins on the same id: it is as fresh as the page; the shared list adds what
 * the trade's read did not carry (a newly created entity). Same item shapes
 * on both sides, so every reader works unchanged.
 */
export const mergeById = <T extends { id?: string }>(own: T[], shared: T[]): T[] => {
  if (shared.length === 0) return own
  if (own.length === 0) return shared
  const byId = new Map<string, T>()
  for (const item of shared) if (item?.id) byId.set(item.id, item)
  for (const item of own) if (item?.id) byId.set(item.id, item)
  return [...byId.values()]
}

const useMerged = <T extends { id?: string }>(
  ownSelector: (state: any) => T[],
  sharedSelector: (state: any) => T[] | undefined,
): T[] => {
  const own = useSelector(ownSelector)
  const shared = useSelector(sharedSelector) ?? []
  return useMemo(() => mergeById(own, shared), [own, shared])
}

export const useTradePageDeals = () => useMerged<any>(getTradeDetailDeals, getDeals as any)
export const useTradePageFacilities = () => useMerged<any>(getTradeDetailFacilities, getFacilities as any)
export const useTradePageEntities = () => useMerged<any>(getTradeDetailEntities, getEntities as any)
export const useTradePageEntityTypes = () => useMerged<any>(getTradeDetailEntityTypes, getEntityTypes as any)
export const useTradePageWireInstructions = () =>
  useMerged<any>(getTradeDetailWireInstructions, getWireInstructions as any)
